/**
 * Nav-Group Proposals Browser (DR-2026-173 — Session L)
 *
 * Operator review surface for AI-proposed `i18n.docs.nav_group` classifications
 * staged in `i18n.nav_group_proposals`. Mirrors the QCFindingsBrowser pattern
 * (DR-2026-051) — same component family, expandable rows, disposition buttons.
 *
 * Phase 1 (calibration) scope: one-at-a-time disposition. No bulk-confirm.
 *
 * Endpoints (mirrored from /@polyglot/qc-findings):
 *   GET    /@polyglot/nav-group-proposals?status=open&source=...&min_confidence=...
 *   PATCH  /@polyglot/nav-group-proposals/:id   — rejected | deferred (no apply)
 *   POST   /@polyglot/nav-group-proposals/:id/apply — confirmed | corrected
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Select,
  Label,
  Icon,
  Button,
  Breadcrumb,
  Message,
  Popup,
  Modal,
  Form,
  TextArea,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PAGE_SIZE = 100;

// Confidence -> color (mirrors the qc-findings severity color idiom)
const confidenceColor = (c) => {
  if (c == null) return 'grey';
  if (c >= 0.85) return 'green';
  if (c >= 0.65) return 'olive';
  if (c >= 0.45) return 'yellow';
  return 'orange';
};

const statusColor = (st) => {
  switch (st) {
    case 'open': return 'blue';
    case 'applied': return 'green';
    case 'rejected': return 'grey';
    case 'stale': return 'orange';
    default: return 'grey';
  }
};

const MIN_CONFIDENCE_OPTIONS = [
  { key: '0',     value: '0',    text: 'Any confidence' },
  { key: '0.25',  value: '0.25', text: '≥ 0.25' },
  { key: '0.50',  value: '0.50', text: '≥ 0.50' },
  { key: '0.65',  value: '0.65', text: '≥ 0.65' },
  { key: '0.75',  value: '0.75', text: '≥ 0.75' },
  { key: '0.85',  value: '0.85', text: '≥ 0.85 (high)' },
  { key: '0.90',  value: '0.90', text: '≥ 0.90 (very high)' },
];

const STATUS_OPTIONS = [
  { key: 'open',     value: 'open',     text: 'Open (awaiting review)' },
  { key: 'applied',  value: 'applied',  text: 'Applied' },
  { key: 'rejected', value: 'rejected', text: 'Rejected' },
  { key: 'stale',    value: 'stale',    text: 'Stale (doc changed)' },
];

const DISPOSITION_DEFS = {
  confirmed: {
    label: 'Confirmed',
    short: 'Operator agrees with proposed nav_group; applied as-is.',
    icon: 'check',
    color: 'green',
  },
  corrected: {
    label: 'Corrected',
    short: 'Operator agrees nav_group is needed but picks a different code.',
    icon: 'edit',
    color: 'teal',
  },
  rejected: {
    label: 'Rejected (no fit)',
    short: 'No single nav_group fits this doc; needs different solution.',
    icon: 'ban',
    color: 'grey',
  },
  deferred: {
    label: 'Deferred',
    short: 'Look again later (e.g. doc is being rewritten).',
    icon: 'clock outline',
    color: 'yellow',
  },
};

const NavGroupProposalsBrowser = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialStatus = searchParams.get('status') || 'open';
  const initialSource = searchParams.get('source') || '';
  const initialMinConfidence = searchParams.get('min_confidence') || '0';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facets, setFacets] = useState({});
  const [navGroups, setNavGroups] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [status, setStatus] = useState(initialStatus);
  const [source, setSource] = useState(initialSource);
  const [minConfidence, setMinConfidence] = useState(initialMinConfidence);

  const [updatingId, setUpdatingId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Per-row "Correct" dropdown selection (pending — not yet POSTed)
  const [correctSelections, setCorrectSelections] = useState({});

  // Reject modal state (collects optional disposition_reason)
  const [rejectModalId, setRejectModalId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const removeFromList = (proposalId) => {
    setItems((prev) => prev.filter((it) => it.id !== proposalId));
    setTotal((prev) => Math.max(0, prev - 1));
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('status', status);
    if (source) params.set('source', source);
    params.set('min_confidence', minConfidence);
    params.set('limit', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@polyglot/nav-group-proposals?${params.toString()}`)
      .then((json) => {
        setItems(json.items || []);
        setTotal(json.items_total || 0);
        setFacets(json.facets || {});
        setNavGroups(json.nav_groups || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [status, source, minConfidence]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (status && status !== 'open') params.set('status', status);
    if (source) params.set('source', source);
    if (minConfidence && minConfidence !== '0') params.set('min_confidence', minConfidence);
    const qs = params.toString();
    history.replace(`/polyglot/nav-group/proposals${qs ? `?${qs}` : ''}`);
  }, [status, source, minConfidence, history]);

  useEffect(() => {
    setExpandedRows(new Set());
    setCorrectSelections({});
  }, [status, source, minConfidence]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyProposal = async (proposalId, disposition, overrideNg) => {
    setUpdatingId(proposalId);
    setActionError(null);
    try {
      const body = { disposition };
      if (disposition === 'corrected') body.apply_nav_group = overrideNg;
      const result = await fetchApi(
        `${getApiPath()}/@polyglot/nav-group-proposals/${proposalId}/apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const newNg = result?.doc?.nav_group || overrideNg;
      removeFromList(proposalId);
      setActionSuccess(
        `Proposal #${proposalId} applied as "${disposition}" — doc.nav_group = ${newNg}`,
      );
      setTimeout(() => setActionSuccess(null), 4000);
    } catch (err) {
      // Staleness path: server returns 409 + {error: 'stale', ...}
      if (err.message === 'stale' || err.message.toLowerCase().includes('stale')) {
        setActionError(
          `Proposal #${proposalId} is stale (doc body changed since proposal). The proposal has been marked stale; refresh and reclassify.`,
        );
        fetchData();
      } else {
        setActionError(`Failed to apply proposal #${proposalId}: ${err.message}`);
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const patchDisposition = async (proposalId, disposition, reason) => {
    setUpdatingId(proposalId);
    setActionError(null);
    try {
      await fetchApi(
        `${getApiPath()}/@polyglot/nav-group-proposals/${proposalId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            disposition,
            ...(reason ? { disposition_reason: reason } : {}),
          }),
        },
      );
      // Rejected → leaves the 'open' filter view; Deferred → stays in 'open'
      if (disposition === 'rejected') {
        removeFromList(proposalId);
        setActionSuccess(`Proposal #${proposalId} rejected.`);
      } else {
        // Deferred: update inline so the operator sees it took
        setItems((prev) =>
          prev.map((it) =>
            it.id === proposalId
              ? {
                  ...it,
                  disposition,
                  disposition_label: DISPOSITION_DEFS[disposition]?.label || disposition,
                }
              : it,
          ),
        );
        setActionSuccess(`Proposal #${proposalId} deferred.`);
      }
      setTimeout(() => setActionSuccess(null), 4000);
    } catch (err) {
      setActionError(`Failed to update proposal #${proposalId}: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const openRejectModal = (proposalId) => {
    setRejectModalId(proposalId);
    setRejectReason('');
  };

  const submitReject = async () => {
    const id = rejectModalId;
    setRejectModalId(null);
    await patchDisposition(id, 'rejected', rejectReason.trim() || null);
  };

  const sourceOptions = [
    { key: '_all', value: '', text: 'All sources' },
    ...(facets.sources || []).map((f) => ({
      key: f.value,
      value: f.value,
      text: `${f.value} (${f.count})`,
    })),
  ];

  return (
    <Container
      style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '1400px' }}
      id="nav-group-proposals-browser"
    >
      <Helmet title="Nav Group Proposals" />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Nav Group Proposals</Breadcrumb.Section>
      </Breadcrumb>

      <div className="polyglot-page-header">
        <h1>Nav Group Proposals</h1>
        <p className="subtitle">
          {total.toLocaleString()} proposals
          {status ? ` — ${status}` : ''}
          {source ? ` — ${source}` : ''}
          {minConfidence && minConfidence !== '0' ? ` — ≥ ${minConfidence}` : ''}
        </p>
        <p style={{ color: '#666', fontSize: '0.95em' }}>
          Session L (DR-2026-173) content-aware nav_group classifier output.
          Phase 1 (calibration): every proposal reviewed one-at-a-time before apply.
        </p>
      </div>

      {actionError && (
        <Message negative onDismiss={() => setActionError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{actionError}</p>
        </Message>
      )}
      {actionSuccess && (
        <Message positive onDismiss={() => setActionSuccess(null)} size="small">
          <Icon name="check circle" /> {actionSuccess}
        </Message>
      )}

      <Segment>
        <div
          style={{
            display: 'flex',
            gap: '0.8em',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Select
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(e, { value }) => setStatus(value)}
            style={{ minWidth: '200px' }}
          />
          <Select
            placeholder="Source"
            options={sourceOptions}
            value={source}
            onChange={(e, { value }) => setSource(value)}
            style={{ minWidth: '220px' }}
          />
          <Select
            placeholder="Min Confidence"
            options={MIN_CONFIDENCE_OPTIONS}
            value={minConfidence}
            onChange={(e, { value }) => setMinConfidence(value)}
            style={{ minWidth: '180px' }}
          />
          <Button basic size="small" onClick={fetchData} style={{ marginLeft: 'auto' }}>
            <Icon name="refresh" /> Refresh
          </Button>
        </div>
      </Segment>

      {error && <Segment color="red">Error: {error}</Segment>}

      {loading ? (
        <Loader active inline="centered" content="Loading proposals..." />
      ) : (
        <Table celled selectable unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: '40px' }} />
              <Table.HeaderCell>Doc</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '160px' }}>Proposed</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '100px' }}>Confidence</Table.HeaderCell>
              <Table.HeaderCell>Rationale (snippet)</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '100px' }}>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((row) => {
              const isExpanded = expandedRows.has(row.id);
              const rationaleSnippet = (row.rationale || '').slice(0, 140) +
                (row.rationale && row.rationale.length > 140 ? '…' : '');
              return (
                <React.Fragment key={row.id}>
                  <Table.Row
                    onClick={() => toggleRow(row.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Cell textAlign="center" style={{ padding: '0.5em 0' }}>
                      <Icon
                        name={isExpanded ? 'chevron down' : 'chevron right'}
                        style={{ color: '#888', margin: 0 }}
                      />
                    </Table.Cell>
                    <Table.Cell style={{ wordBreak: 'break-word' }}>
                      <div style={{ fontWeight: 600 }}>{row.doc_title}</div>
                      <div style={{ fontSize: '0.85em', color: '#888' }}>
                        {row.doc_slug}
                      </div>
                      {row.doc_current_nav_group && (
                        <div style={{ fontSize: '0.85em', color: '#999', marginTop: '0.2em' }}>
                          (currently: {row.doc_current_nav_group})
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {row.proposed_nav_group ? (
                        <Label size="small" color="blue">
                          {row.proposed_nav_group_label || row.proposed_nav_group}
                        </Label>
                      ) : (
                        <Label size="small" color="grey" basic>
                          no-fit
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="small" color={confidenceColor(row.confidence)}>
                        {Number(row.confidence).toFixed(3)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell style={{ wordBreak: 'break-word', color: '#555' }}>
                      {rationaleSnippet}
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="small" color={statusColor(row.status)}>
                        {row.status_label || row.status}
                      </Label>
                    </Table.Cell>
                  </Table.Row>

                  {isExpanded && (
                    <Table.Row>
                      <Table.Cell colSpan="6" style={{ background: '#f8f9fa' }}>
                        <ExpandedDetail
                          row={row}
                          navGroups={navGroups}
                          updatingId={updatingId}
                          correctSelections={correctSelections}
                          setCorrectSelections={setCorrectSelections}
                          applyProposal={applyProposal}
                          patchDisposition={patchDisposition}
                          openRejectModal={openRejectModal}
                        />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
            {items.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">
                  <Segment placeholder>
                    <Header icon>
                      <Icon name="search" />
                      No proposals match the current filters
                    </Header>
                  </Segment>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}

      {/* Reject reason modal */}
      <Modal
        open={rejectModalId !== null}
        onClose={() => setRejectModalId(null)}
        size="small"
      >
        <Modal.Header>Reject Proposal #{rejectModalId}</Modal.Header>
        <Modal.Content>
          <p>
            Reject this proposal — no single nav_group fits this doc.
            The proposal will be closed (status = rejected); the doc.nav_group is not touched.
          </p>
          <Form>
            <Form.Field>
              <label>Reason (optional)</label>
              <TextArea
                rows={3}
                value={rejectReason}
                onChange={(e, { value }) => setRejectReason(value)}
                placeholder="Why doesn't a nav_group fit?"
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setRejectModalId(null)}>Cancel</Button>
          <Button color="grey" onClick={submitReject}>
            <Icon name="ban" /> Reject
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

const ExpandedDetail = ({
  row,
  navGroups,
  updatingId,
  correctSelections,
  setCorrectSelections,
  applyProposal,
  patchDisposition,
  openRejectModal,
}) => {
  const isOpen = row.status === 'open';
  const currentCorrect = correctSelections[row.id] || '';
  const navGroupOptions = navGroups.map((ng) => ({
    key: ng.code,
    value: ng.code,
    text: `${ng.label} (${ng.code})`,
  }));

  const alts = Array.isArray(row.ambiguous_alternatives)
    ? row.ambiguous_alternatives
    : [];

  const bodyPreview = row.doc_body_preview || '';
  const bodyTruncated = (row.doc_body_length || 0) > bodyPreview.length;

  return (
    <div style={{ padding: '0.8em 1em' }}>
      {/* Rationale (full) */}
      <div style={{ marginBottom: '0.8em' }}>
        <SectionLabel>Rationale</SectionLabel>
        <div style={{ fontSize: '1.05em', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
          {row.rationale}
        </div>
      </div>

      {/* Doc body preview */}
      <div style={{ marginBottom: '0.8em' }}>
        <SectionLabel>Doc Body (preview)</SectionLabel>
        <div
          style={{
            fontSize: '0.92em',
            fontFamily: 'monospace',
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            padding: '0.6em 0.8em',
            maxHeight: '300px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {bodyPreview || <em style={{ color: '#999' }}>(no body)</em>}
          {bodyTruncated && (
            <div style={{ color: '#888', marginTop: '0.5em' }}>
              &hellip; ({row.doc_body_length} chars total) &mdash;{' '}
              <Link to={`/docs/${row.doc_slug}`}>Open full doc</Link>
            </div>
          )}
        </div>
      </div>

      {/* Ambiguous alternatives */}
      {alts.length > 0 && (
        <div style={{ marginBottom: '0.8em' }}>
          <SectionLabel>Ambiguous Alternatives</SectionLabel>
          <Table compact size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>nav_group</Table.HeaderCell>
                <Table.HeaderCell>Confidence</Table.HeaderCell>
                <Table.HeaderCell>Why</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {alts.map((alt, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <Label size="small" basic>{alt.nav_group}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    {alt.confidence != null
                      ? Number(alt.confidence).toFixed(3)
                      : <span style={{ color: '#ccc' }}>&mdash;</span>}
                  </Table.Cell>
                  <Table.Cell style={{ wordBreak: 'break-word' }}>
                    {alt.why}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Provenance row */}
      <div
        style={{
          display: 'flex',
          gap: '2em',
          flexWrap: 'wrap',
          fontSize: '0.9em',
          color: '#666',
          borderTop: '1px solid #e8e8e8',
          paddingTop: '0.6em',
          marginTop: '0.4em',
        }}
      >
        <span><strong>Source:</strong> {row.source}</span>
        <span><strong>Batch:</strong> {row.batch_id}</span>
        {row.discovered_at && (
          <span>
            <strong>Discovered:</strong>{' '}
            {new Date(row.discovered_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Audit trail (if dispositioned) */}
      {row.disposition && (
        <div
          style={{
            marginTop: '0.6em',
            padding: '0.6em 0.8em',
            background: '#f5f5f5',
            borderRadius: '4px',
          }}
        >
          <SectionLabel>Audit Trail</SectionLabel>
          <div style={{ display: 'flex', gap: '1.5em', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              <Icon name="user" style={{ color: '#555' }} />
              <strong>{row.disposition_by || 'unknown'}</strong>
            </span>
            <span>
              marked as{' '}
              <Label size="mini" color={DISPOSITION_DEFS[row.disposition]?.color}>
                {row.disposition_label || row.disposition}
              </Label>
            </span>
            {row.disposition_at && (
              <span style={{ color: '#888' }}>
                <Icon name="clock outline" />
                {new Date(row.disposition_at).toLocaleString()}
              </span>
            )}
            {row.applied_nav_group && (
              <span style={{ color: '#888' }}>
                <Icon name="check circle" />
                Applied as <strong>{row.applied_nav_group}</strong>
              </span>
            )}
          </div>
          {row.disposition_reason && (
            <div style={{ marginTop: '0.4em', color: '#555' }}>
              <strong>Reason:</strong> {row.disposition_reason}
            </div>
          )}
        </div>
      )}

      {/* Disposition action row */}
      {isOpen && (
        <div
          style={{
            display: 'flex',
            gap: '0.6em',
            alignItems: 'center',
            borderTop: '1px solid #e8e8e8',
            paddingTop: '0.8em',
            marginTop: '0.6em',
            flexWrap: 'wrap',
          }}
        >
          <SectionLabel style={{ marginBottom: 0, marginRight: '0.3em' }}>
            Disposition
          </SectionLabel>

          {/* Confirm: apply with proposed_nav_group */}
          <Popup
            trigger={
              <span>
                <Button
                  size="small"
                  color="green"
                  loading={updatingId === row.id}
                  disabled={updatingId === row.id || !row.proposed_nav_group}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyProposal(row.id, 'confirmed', null);
                  }}
                >
                  <Icon name="check" /> Confirm
                </Button>
              </span>
            }
            content={
              row.proposed_nav_group
                ? DISPOSITION_DEFS.confirmed.short
                : 'Cannot confirm a no-fit proposal (proposed_nav_group is null). Use Correct or Reject.'
            }
            size="small"
            position="top center"
            inverted
          />

          {/* Correct: dropdown + apply */}
          <Popup
            trigger={
              <span style={{ display: 'inline-flex', gap: '0.3em', alignItems: 'center' }}>
                <Select
                  compact
                  placeholder="Pick nav_group..."
                  options={navGroupOptions}
                  value={currentCorrect}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e, { value }) =>
                    setCorrectSelections((prev) => ({ ...prev, [row.id]: value }))
                  }
                />
                <Button
                  size="small"
                  color="teal"
                  loading={updatingId === row.id}
                  disabled={updatingId === row.id || !currentCorrect}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyProposal(row.id, 'corrected', currentCorrect);
                  }}
                >
                  <Icon name="edit" /> Correct
                </Button>
              </span>
            }
            content={DISPOSITION_DEFS.corrected.short}
            size="small"
            position="top center"
            inverted
          />

          {/* Reject (opens modal) */}
          <Popup
            trigger={
              <Button
                size="small"
                color="grey"
                basic
                loading={updatingId === row.id}
                disabled={updatingId === row.id}
                onClick={(e) => {
                  e.stopPropagation();
                  openRejectModal(row.id);
                }}
              >
                <Icon name="ban" /> Reject
              </Button>
            }
            content={DISPOSITION_DEFS.rejected.short}
            size="small"
            position="top center"
            inverted
          />

          {/* Defer */}
          <Popup
            trigger={
              <Button
                size="small"
                color="yellow"
                basic
                loading={updatingId === row.id}
                disabled={updatingId === row.id}
                onClick={(e) => {
                  e.stopPropagation();
                  patchDisposition(row.id, 'deferred', null);
                }}
              >
                <Icon name="clock outline" /> Defer
              </Button>
            }
            content={DISPOSITION_DEFS.deferred.short}
            size="small"
            position="top center"
            inverted
          />
        </div>
      )}
    </div>
  );
};

const SectionLabel = ({ children, style }) => (
  <strong
    style={{
      display: 'block',
      marginBottom: '0.3em',
      color: '#555',
      fontSize: '0.85em',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      ...style,
    }}
  >
    {children}
  </strong>
);

export default NavGroupProposalsBrowser;
